import { GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { ModalFormDrawer, PageSectionHeading } from 'Components/Display';
import {
  AsyncGrid,
  FileDownloadCell,
  FilterBarMainGrid,
  getFormattedDisplayCell,
  getSeeMoreCell,
} from 'Components/Grid';
import DownloadIcon from 'Assets/Icons/download.svg';
import {
  GridProvider,
  ModalFormProvider,
  useEntity,
  useGridState,
  useModalFormState,
  useToasts,
} from 'Context';
import {
  ActionsDropdownConfig,
  AppointmentIdGridSearchModel,
  defaultGridProps,
  GridColumnWidth,
  ReferralIdGridSearchModel,
  ReferralRawDocumentModel,
} from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { Fragment } from 'react';
import {
  ReferralRawDocumentActionCell,
  ReferralRawDocumentDrawerFormContent,
} from '.';
import { dayjs } from 'Utilities/DateTimeUtc';
import { DEFAULT_DATETIME24H_FORMAT, DEFAULT_DATETIME_FORMAT } from 'Utilities';

export const MedicalRecords: React.FC = () => {
  const toast = useToasts();
  const entity = useEntity();
  const { refreshGrid, entityName } = useGridState();

  const handleSuccess = () => {
    toast.success(`${entityName} saved!`);
    refreshGrid();
  };

  let appointmentStatusValue =
    entity.appointment !== undefined &&
    entity?.appointment?.appointmentStatus != null
      ? entity?.appointment?.appointmentStatus?.value
      : null;
  const baseApi = ENDPOINTS.appointmentdocuments;
  var formModel = new ReferralRawDocumentModel(entity.id);

  return (
    <ModalFormProvider
      formModel={formModel}
      endpoint={baseApi.base}
      handleSuccess={handleSuccess}
    >
      <ReferralRawDocumentGridWithModalDrawer />
    </ModalFormProvider>
  );
};
export enum AppointmentStatusId {
  Pending_Scheduling = 1,
  Assigned_Under_Review = 2,
  In_QA = 3,
  Re_Opened = 4,
  Closed = 5,
  Cancelled = 6,
  Pending_Medicals = 7,
}

const ReferralRawDocumentGridWithModalDrawer: React.FC = () => {
  const entity = useEntity();
  const { setupAdd } = useModalFormState();
  const searchModel: AppointmentIdGridSearchModel =
    new AppointmentIdGridSearchModel(entity.appointment?.id);
  let appointmentStatusValue =
    entity.appointment !== undefined &&
    entity?.appointment?.appointmentStatus != null
      ? entity?.appointment?.appointmentStatus?.value
      : null;
  const baseApi = ENDPOINTS.appointmentdocuments;

  const isClosedOrCancelled =
    appointmentStatusValue === AppointmentStatusId.Closed ||
    appointmentStatusValue === AppointmentStatusId.Cancelled;
  const isUnderReview =
    appointmentStatusValue === AppointmentStatusId.Assigned_Under_Review;

  const actionsConfig: ActionsDropdownConfig = {
    name: 'Actions',
    items: [
      {
        name: 'Upload Documents',
        onClick: setupAdd,
        permissions: [
          Permission.AddReferralRawDocument,
          Permission.UploadReferralRawDocument,
        ],
      },
    ],
  };

  return (
    <div style={{ marginTop: 20 }}>
      <FilterBarMainGrid
        headerDisabled
        isMainGrid
        fileUploaderDisabledLabel={
          isUnderReview
            ? 'Upload is currently unavailable as your case is being reviewed. For assistance, please email your designated team.'
            : undefined
        }
        fileUploaderEndpoint={!isClosedOrCancelled ? baseApi.base : undefined}
        fileUploaderFormEndpoint={
          !isClosedOrCancelled ? ENDPOINTS.appointmentdocuments.base : undefined
        }
        headerTitle="Medical Records"
        filterBarProps={{
          filterConfigurations: [],
          filterValues: {},
          isColumnSelectorEnabled: false,
        }}
        readEndpoint={baseApi.read}
        search={searchModel}
        {...defaultGridProps}
        pageSize={25}
        enableGridRefresh={true}
        style={{ cursor: 'pointer' }}
        uploadExtraProps={{
          appointmentId: entity.appointment?.id,
          documentDate: new Date().toISOString(),
        }}
      >
        <GridColumn
          field="fileName"
          title="File Name"
          cell={FileNameWithoutExtensionCell}
          width={GridColumnWidth.MedLarge}
        />
        <GridColumn
          field="extension"
          title="File Format"
          cell={ExtensionsCell}
          sortable={false}
        />
        <GridColumn field="size" title="File Size" cell={FileSizeCell} />
        <GridColumn field="uploadedBy" title="Source" sortable={false} />
        <GridColumn
          cell={DateAndTimeFormatCell}
          field="uploadDate"
          title="Date & Time Uploaded"
          width={'auto'}
        />

        <GridColumn
          cell={(p) => <ActionsCell {...p} baseApi={baseApi} />}
          field="actions"
          title="Actions"
          sortable={false}
        />
      </FilterBarMainGrid>
      <ModalFormDrawer width={500} title="Case Attachment">
        <ReferralRawDocumentDrawerFormContent />
      </ModalFormDrawer>
    </div>
  );
};

const FileNameWithoutExtensionCell = (props: GridCellProps) => {
  const fileNameWithExtension = props.dataItem.fileName;
  const fileNameWithoutExtension = fileNameWithExtension.replace(
    /\.[^/.]+$/,
    ''
  );
  return <td>{fileNameWithoutExtension}</td>;
};

const ExtensionsCell: React.FC<GridCellProps> = (props) => {
  const extension = props.dataItem.extension;
  return <td>{extension ? extension.toUpperCase().replace('.', '') : ''}</td>;
};
const DateAndTimeFormatCell: React.FC<GridCellProps> = (props) => {
  const dateValue = props.dataItem.uploadDate;
  const formatted = dateValue
    ? dayjs.utc(dateValue).local().format(DEFAULT_DATETIME_FORMAT)
    : '';

  return <td>{formatted}</td>;
};
const FileSizeCell: React.FC<GridCellProps> = (props) => {
  const fileSize = props.dataItem.size;
  if (fileSize) {
    const sizeInKB = (fileSize / 1024).toFixed(2);
    return <td>{`${sizeInKB} KB`}</td>;
  }
  return <td>0 KB</td>;
};

const ActionsCell: React.FC<
  GridCellProps & { baseApi: typeof ENDPOINTS.appointmentdocuments }
> = (props) => {
  return (
    <FileDownloadCell fileControllerEndpoint={props.baseApi.base} {...props}>
      <img src={DownloadIcon} alt="downloadIcon" />
    </FileDownloadCell>
  );
};
